<template>
  <div
    class="dual-list-box"
    :class="{ 'dual-list-box--disabled': disabled }"
  >
    <div
      v-if="!disabled"
      class="dual-list-box__column"
    >
      <div class="dual-list-box__header">
        Available <span class="dual-list-box__count text-body-secondary">{{ availableCountLabel }}</span>
      </div>
      <CFormInput
        v-if="!hideSearch"
        v-model="availableSearch"
        size="sm"
        type="search"
        :placeholder="searchPlaceholder"
        class="mb-2"
      />
      <CListGroup
        as="div"
        class="dual-list-box__list"
        :style="{ height: listHeight }"
      >
        <template
          v-for="group in availableVisible.groups"
          :key="`available-${group.label}`"
        >
          <CListGroupItem
            v-if="group.label"
            class="dual-list-box__group-label"
          >
            {{ group.label }}
          </CListGroupItem>
          <CListGroupItem
            v-for="option in group.options"
            :key="option.value"
            as="button"
            type="button"
            class="dual-list-box__option"
            :title="option.label"
            @click="select(option)"
          >
            {{ option.label }}
          </CListGroupItem>
        </template>
        <CListGroupItem
          v-if="availableVisible.hiddenCount"
          class="dual-list-box__overflow text-body-secondary"
        >
          {{ availableVisible.hiddenCount }} more — refine the search
        </CListGroupItem>
        <div
          v-if="!availableVisible.count"
          class="dual-list-box__empty text-body-secondary"
        >
          {{ availableEmptyLabel }}
        </div>
      </CListGroup>
      <CButton
        color="link"
        size="sm"
        class="dual-list-box__bulk"
        :disabled="!availableVisible.count"
        @click="selectAll"
      >
        Select all{{ availableSearch ? ' matching' : '' }}
      </CButton>
    </div>

    <div class="dual-list-box__column">
      <div class="dual-list-box__header">
        Selected <span class="dual-list-box__count text-body-secondary">{{ selectedCountLabel }}</span>
      </div>
      <CFormInput
        v-if="!hideSearch && !disabled"
        v-model="selectedSearch"
        size="sm"
        type="search"
        :placeholder="selectedSearchPlaceholder"
        class="mb-2"
      />
      <CListGroup
        as="div"
        class="dual-list-box__list"
        :style="{ height: listHeight }"
      >
        <template
          v-for="group in selectedVisible.groups"
          :key="`selected-${group.label}`"
        >
          <CListGroupItem
            v-if="group.label"
            class="dual-list-box__group-label"
          >
            {{ group.label }}
          </CListGroupItem>
          <CListGroupItem
            v-for="option in group.options"
            :key="option.value"
            as="button"
            type="button"
            class="dual-list-box__option dual-list-box__option--selected"
            :disabled="disabled"
            :title="option.label"
            @click="deselect(option)"
          >
            {{ option.label }}
          </CListGroupItem>
        </template>
        <CListGroupItem
          v-if="selectedVisible.hiddenCount"
          class="dual-list-box__overflow text-body-secondary"
        >
          {{ selectedVisible.hiddenCount }} more — refine the search
        </CListGroupItem>
        <div
          v-if="!selectedVisible.count"
          class="dual-list-box__empty text-body-secondary"
        >
          {{ selectedEmptyLabel }}
        </div>
      </CListGroup>
      <CButton
        v-if="!disabled"
        color="link"
        size="sm"
        class="dual-list-box__bulk"
        :disabled="!selectedVisible.count"
        @click="deselectAll"
      >
        Deselect all{{ selectedSearch ? ' matching' : '' }}
      </CButton>
    </div>
  </div>
</template>

<script>
// Two-column pick list: available options on the left, selected on the right,
// click an item to move it across. Replaces the pre-Vue3 vue-multiselect-listbox.
//
// Contract mirrors CMultiSelect so the two stay interchangeable at call sites:
// `options` is flat [{ value, label }] or grouped [{ label, options: [...] }],
// `value` is the array of selected values, and `change` emits the selected
// option objects. Fully controlled: the selection shown always derives from
// `value`, so external clears need no remount hacks.
//
// `value` entries with no matching option stay visible as "Unknown" rows and
// survive a `change` round-trip untouched — replace-all save flows must not
// silently revoke a selection just because its option dropped out of the
// current option set.
const UNKNOWN_LABEL = value => `Unknown (${value})`

export default {
  name: 'DualListBox',

  props: {
    options: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    // Read-only mode: only the Selected column renders, without search
    // or bulk actions.
    disabled: {
      type: Boolean,
      default: false
    },
    hideSearch: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: 'Search available'
    },
    selectedSearchPlaceholder: {
      type: String,
      default: 'Search selected'
    },
    emptyText: {
      type: String,
      default: 'No options'
    },
    selectedEmptyText: {
      type: String,
      default: 'Nothing selected'
    },
    listHeight: {
      type: String,
      default: '16rem'
    },
    // Rendering cap per column; everything stays reachable through search.
    // Keeps unbounded lists (e.g. seller accounts) from mounting thousands
    // of DOM nodes at once.
    maxVisible: {
      type: Number,
      default: 200
    }
  },

  emits: ['change'],

  data() {
    return {
      availableSearch: '',
      selectedSearch: ''
    }
  },

  computed: {
    // Normalize the two accepted option shapes into [{ label, options }];
    // flat lists become a single unlabeled group.
    groups() {
      if (this.options.some(o => Array.isArray(o?.options))) {
        return this.options.map(g => ({
          label: g.label,
          options: Array.isArray(g?.options) ? g.options : []
        }))
      }

      return [{ label: '', options: this.options }]
    },

    flatOptions() {
      return this.groups.flatMap(g => g.options)
    },

    knownValues() {
      return new Set(this.flatOptions.map(o => o.value))
    },

    selectedValues() {
      return new Set(this.value)
    },

    unknownOptions() {
      return this.value
        .filter(v => !this.knownValues.has(v))
        .map(v => ({ value: v, label: UNKNOWN_LABEL(v) }))
    },

    // Selected column source: known groups plus a trailing pseudo-group for
    // values the current option set no longer contains.
    selectedSourceGroups() {
      if (!this.unknownOptions.length) {
        return this.groups
      }

      return [...this.groups, { label: '', options: this.unknownOptions, unknown: true }]
    },

    totalAvailable() {
      return this.flatOptions.filter(o => !this.selectedValues.has(o.value)).length
    },

    totalSelected() {
      return this.value.length
    },

    availableVisible() {
      return this.capGroups(
        this.filterGroups(this.groups, o => !this.selectedValues.has(o.value), this.availableSearch)
      )
    },

    selectedVisible() {
      return this.capGroups(
        this.filterGroups(this.selectedSourceGroups, o => this.selectedValues.has(o.value), this.selectedSearch)
      )
    },

    // Header shows the true totals; during a search it becomes "matches of total".
    availableCountLabel() {
      return this.availableSearch
        ? `${this.availableVisible.count} of ${this.totalAvailable}`
        : `${this.totalAvailable}`
    },

    selectedCountLabel() {
      return this.selectedSearch
        ? `${this.selectedVisible.count} of ${this.totalSelected}`
        : `${this.totalSelected}`
    },

    // "No matches" only applies when the search filtered something out; an
    // exhausted or empty column keeps its own message even while a term is typed.
    availableEmptyLabel() {
      if (!this.flatOptions.length) {
        return this.emptyText
      }

      return this.totalAvailable ? 'No matches' : 'All selected'
    },

    selectedEmptyLabel() {
      return this.totalSelected ? 'No matches' : this.selectedEmptyText
    }
  },

  watch: {
    // A swapped option set (call type / traffic source type switches) makes a
    // typed search stale — the new list would show "No matches" for no visible
    // reason — so reset both searches.
    options() {
      this.availableSearch = ''
      this.selectedSearch = ''
    }
  },

  methods: {
    filterGroups(groups, belongs, search) {
      const query = search.trim().toLowerCase()

      return groups
        .map(group => ({
          ...group,
          options: group.options.filter(
            o => belongs(o) && (!query || String(o.label).toLowerCase().includes(query))
          )
        }))
        .filter(group => group.options.length)
    },

    capGroups(groups) {
      const count = groups.reduce((sum, group) => sum + group.options.length, 0)

      if (count <= this.maxVisible) {
        return { groups, count, hiddenCount: 0 }
      }

      let remaining = this.maxVisible
      const capped = []

      for (const group of groups) {
        if (!remaining) {
          break
        }

        capped.push({ ...group, options: group.options.slice(0, remaining) })
        remaining -= Math.min(group.options.length, remaining)
      }

      return { groups: capped, count, hiddenCount: count - this.maxVisible }
    },

    emitChange(selectedValues) {
      const known = this.flatOptions.filter(o => selectedValues.has(o.value))
      const unknown = this.unknownOptions.filter(o => selectedValues.has(o.value))

      this.$emit('change', [...known, ...unknown])
    },

    select(option) {
      this.emitChange(new Set([...this.value, option.value]))
    },

    deselect(option) {
      const next = new Set(this.value)
      next.delete(option.value)
      this.emitChange(next)
    },

    // Bulk actions operate on what matches the search, not just the capped
    // visible slice, so "Select all matching" moves every match.
    selectAll() {
      const next = new Set(this.value)
      this.filterGroups(this.groups, o => !this.selectedValues.has(o.value), this.availableSearch)
        .forEach(group => group.options.forEach(o => next.add(o.value)))
      this.emitChange(next)
    },

    deselectAll() {
      const next = new Set(this.value)
      this.filterGroups(this.selectedSourceGroups, o => this.selectedValues.has(o.value), this.selectedSearch)
        .forEach(group => group.options.forEach(o => next.delete(o.value)))
      this.emitChange(next)
    }
  }
}
</script>

<style lang="scss" scoped>
.dual-list-box {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 400px));
  gap: 1rem;

  @media (max-width: 575.98px) {
    grid-template-columns: minmax(0, 400px);
  }
}

.dual-list-box--disabled {
  grid-template-columns: minmax(0, 400px);
}

.dual-list-box__header {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.dual-list-box__count {
  font-size: 0.75rem;
  font-weight: 400;
}

.dual-list-box__list {
  overflow-y: auto;
  border: 1px solid var(--cui-border-color);
  border-radius: var(--cui-border-radius);
}

.dual-list-box__option {
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  text-align: start;
  border-inline: 0;

  &:first-child {
    border-top: 0;
  }
}

.dual-list-box__group-label {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--cui-secondary-color);
  background-color: var(--cui-tertiary-bg);
  border-inline: 0;

  &:first-child {
    border-top: 0;
  }
}

.dual-list-box__overflow {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  border-inline: 0;
}

.dual-list-box__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
}

.dual-list-box__bulk {
  padding-inline: 0;
  text-decoration: none;
}

.dual-list-box--disabled .dual-list-box__list {
  background-color: var(--cui-secondary-bg);
}
</style>
